import React from 'react';
import { SortingCanvasComponent } from './SortingCanvasComponent';
import { SpatialCanvasComponent } from './SpatialCanvasComponent';
import { PatternMakerComponent } from './PatternMakerComponent';
import { DrawingBoardComponent } from './DrawingBoardComponent';
import { ObjectManipulatorComponent } from './ObjectManipulatorComponent';

export const GPFResourceDemo: React.FC = () => {
  const handleResourceAction = (resourceId: string, action: string, data?: any) => {
    console.log('Resource Action:', { resourceId, action, data });
  };

  const sortingCanvasResource = {
    type: 'sorting_canvas',
    name: 'Demo Sorting Canvas',
    description: 'Triez les objets selon la couleur et la forme',
    objects: [
      { type: 'circle', count: 3, color: 'red' },
      { type: 'square', count: 3, color: 'blue' },
      { type: 'triangle', count: 3, color: 'green' }
    ],
    categories: ['couleur', 'forme'],
    showCount: true,
    allowInteraction: true
  };

  const spatialCanvasResource = {
    type: 'spatial_canvas',
    name: 'Demo Spatial Canvas',
    description: 'Placez les objets selon les consignes spatiales',
    gridSize: '5x5',
    objects: [
      { id: 'star', type: 'star', position: 'center', color: 'yellow' },
      { id: 'circle', type: 'circle', position: 'left', color: 'red' },
      { id: 'square', type: 'square', position: 'top', color: 'blue' },
      { id: 'triangle', type: 'triangle', position: 'right', color: 'green' }
    ],
    directions: ['devant', 'derrière', 'gauche', 'droite', 'haut', 'bas'],
    showGrid: true,
    allowInteraction: true
  };

  const patternMakerResource = {
    type: 'pattern_maker',
    name: 'Demo Pattern Maker',
    question: 'Reproduisez le rythme affiché en utilisant les éléments disponibles',
    pattern: [
      { id: 'p1', emoji: '🔴', color: '#EF4444', type: 'shape' as const },
      { id: 'p2', emoji: '🔵', color: '#3B82F6', type: 'shape' as const },
      { id: 'p3', emoji: '🔴', color: '#EF4444', type: 'shape' as const },
      { id: 'p4', emoji: '🔵', color: '#3B82F6', type: 'shape' as const }
    ],
    options: [
      { id: 'o1', emoji: '🔴', color: '#EF4444', type: 'shape' as const },
      { id: 'o2', emoji: '🔵', color: '#3B82F6', type: 'shape' as const },
      { id: 'o3', emoji: '🟡', color: '#F59E0B', type: 'shape' as const },
      { id: 'o4', emoji: '🟢', color: '#10B981', type: 'shape' as const }
    ],
    maxLength: 8,
    showHint: true,
    allowInteraction: true
  };

  const drawingBoardResource = {
    type: 'drawing_board',
    name: 'Demo Drawing Board',
    description: 'Planche de dessin interactive avec outils',
    gridSize: '10x10',
    tools: ['crayon', 'gomme', 'règle', 'formes'],
    templates: [],
    allowInteraction: true
  };

  const objectManipulatorResource = {
    type: 'object_manipulator',
    name: 'Demo Object Manipulator',
    description: 'Manipulez, faites pivoter et redimensionnez les objets',
    objects: [
      { id: 'obj1', type: 'circle', color: 'red' },
      { id: 'obj2', type: 'square', color: 'blue' },
      { id: 'obj3', type: 'triangle', color: 'green' }
    ],
    transformations: [
      { type: 'move', value: 50, direction: 'right' },
      { type: 'rotate', value: 90 },
      { type: 'scale', value: 1.2 }
    ],
    allowInteraction: true
  };

  return (
    <div className="p-6 space-y-8">
      <div className="text-center mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-4">
          GPF Resource Components Demo
        </h1>
        <p className="text-lg text-gray-600">
          Explorez tous les nouveaux composants de ressources GPF
        </p>
      </div>

      {/* Sorting Canvas */}
      <div className="border rounded-lg p-6 bg-white shadow-sm">
        <h2 className="text-2xl font-semibold text-gray-900 mb-4">
          1. Sorting Canvas Component
        </h2>
        <SortingCanvasComponent
          resource={sortingCanvasResource}
          component={{ type: 'manipulative', component_id: 'demo_sorting' }}
          onAction={handleResourceAction}
        />
      </div>

      {/* Spatial Canvas */}
      <div className="border rounded-lg p-6 bg-white shadow-sm">
        <h2 className="text-2xl font-semibold text-gray-900 mb-4">
          2. Spatial Canvas Component
        </h2>
        <SpatialCanvasComponent
          resource={spatialCanvasResource}
          component={{ type: 'manipulative', component_id: 'demo_spatial' }}
          onAction={handleResourceAction}
        />
      </div>

      {/* Pattern Maker */}
      <div className="border rounded-lg p-6 bg-white shadow-sm">
        <h2 className="text-2xl font-semibold text-gray-900 mb-4">
          3. Pattern Maker Component
        </h2>
        <PatternMakerComponent
          resource={patternMakerResource}
          onAction={handleResourceAction}
        />
      </div>

      {/* Drawing Board */}
      <div className="border rounded-lg p-6 bg-white shadow-sm">
        <h2 className="text-2xl font-semibold text-gray-900 mb-4">
          4. Drawing Board Component
        </h2>
        <DrawingBoardComponent
          resource={drawingBoardResource}
          component={{ type: 'manipulative', component_id: 'demo_drawing' }}
          onAction={handleResourceAction}
        />
      </div>

      {/* Object Manipulator */}
      <div className="border rounded-lg p-6 bg-white shadow-sm">
        <h2 className="text-2xl font-semibold text-gray-900 mb-4">
          5. Object Manipulator Component
        </h2>
        <ObjectManipulatorComponent
          resource={objectManipulatorResource}
          component={{ type: 'manipulative', component_id: 'demo_manipulator' }}
          onAction={handleResourceAction}
        />
      </div>

      {/* Usage Instructions */}
      <div className="border rounded-lg p-6 bg-blue-50">
        <h2 className="text-2xl font-semibold text-blue-900 mb-4">
          Comment utiliser ces composants
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h3 className="text-lg font-medium text-blue-800 mb-2">Dans les leçons GPF</h3>
            <ul className="text-blue-700 space-y-1">
              <li>• Ajoutez ces ressources via le sélecteur de ressources</li>
              <li>• Configurez-les selon vos besoins pédagogiques</li>
              <li>• Intégrez-les dans vos étapes de timeline</li>
              <li>• Utilisez-les pour l'évaluation formative</li>
            </ul>
          </div>
          <div>
            <h3 className="text-lg font-medium text-blue-800 mb-2">Fonctionnalités clés</h3>
            <ul className="text-blue-700 space-y-1">
              <li>• Interaction tactile et visuelle</li>
              <li>• Feedback en temps réel</li>
              <li>• Configuration flexible</li>
              <li>• Intégration avec le système de suivi</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};
